/**
 * Вкладка «Атрибуты» окна товара: правка наименования, категории, штрихкода и
 * у пряников — веса теста (сохраняются кнопкой «Сохранить» футера) и справа —
 * данные макета-донора только для чтения с кнопкой «Открыть макет».
 */

import React from 'react'
import { ExternalLink } from 'lucide-react'
import { Badge } from '../../../../components/ui/Badge'
import { Button } from '../../../../components/ui/Button'
import { CopyButton } from '../../../../components/ui/CopyButton'
import { Select } from '../../../../components/ui/Select'
import { cn } from '../../../../lib/cn'
import { ONE_C_INFO, formatPrice, isNoDesignCode } from '../../lib'
import type { ProductV2 } from '../../types'
import { isManualProduct, themeNumberOfProduct } from './model'
import { Row, SectionTitle, ValueOrNoData } from './parts'

export interface EditableForm {
  title: string
  barcode: string
  category_id: string
  /** Вес теста, граммы, строкой поля ввода; пусто — не задан. */
  dough_weight_g: string
}

/** Вес теста для поля ввода: «246», «312.5», пусто — не задан. */
export function doughWeightText(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return ''
  const weight = Number(value)
  return Number.isFinite(weight) ? String(Math.round(weight * 10) / 10) : ''
}

/** Пряники — категории PP0/PP1/PP2: только у них в 1С есть строка теста. */
function isGingerbread(product: ProductV2): boolean {
  return /^PP/i.test(product.category?.prefix || '') || /^PP/i.test(product.article)
}

interface AttrsTabProps {
  product: ProductV2
  form: EditableForm
  onChange: (patch: Partial<EditableForm>) => void
  categories: Array<{ id: number; description: string }>
  /** Открыть макет в окне навигатора; без навигатора кнопки нет. */
  onOpenDesign?: () => void
}

const fieldClass =
  'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100'

const labelClass = 'mb-1 block text-xs font-medium text-gray-700 dark:text-zinc-300'

export const AttrsTab: React.FC<AttrsTabProps> = ({ product, form, onChange, categories, onOpenDesign }) => {
  const manual = isManualProduct(product)
  const design = product.design
  const noDesign = isNoDesignCode(design?.design_code) || isNoDesignCode(product.design_id)
  const themeNumber = themeNumberOfProduct(product)
  const gingerbread = isGingerbread(product)
  const fixedDough = doughWeightText(product.category?.dough_fixed_weight_g)
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="space-y-3">
        <div>
          <label className={labelClass} htmlFor="product-title">Наименование</label>
          <textarea
            id="product-title"
            rows={2}
            value={form.title}
            onChange={(event) => onChange({ title: event.target.value })}
            className={fieldClass}
          />
        </div>
        <div>
          <label className={labelClass}>Категория</label>
          <Select value={form.category_id} onValueChange={(value) => onChange({ category_id: value })}>
            <option value="">Не выбрана</option>
            {categories.map((category) => (
              <option key={category.id} value={String(category.id)}>
                {category.description}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label className={labelClass} htmlFor="product-barcode">Штрихкод</label>
          <input
            id="product-barcode"
            value={form.barcode}
            onChange={(event) => onChange({ barcode: event.target.value })}
            placeholder="не задан"
            className={cn(fieldClass, 'font-mono')}
          />
        </div>
        {gingerbread && (
          fixedDough ? (
            <div>
              <span className={labelClass}>Вес теста</span>
              <p className="text-sm tabular-nums text-gray-900 dark:text-zinc-100">{fixedDough} г</p>
              <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                Постоянный вес категории: от артикула не зависит, меняется в настройках категории.
              </p>
            </div>
          ) : (
            <div>
              <label className={labelClass} htmlFor="product-dough-weight">Вес теста, г</label>
              <input
                id="product-dough-weight"
                type="number"
                min="0"
                step="0.1"
                inputMode="decimal"
                value={form.dough_weight_g}
                onChange={(event) => onChange({ dough_weight_g: event.target.value })}
                placeholder="не задан"
                className={cn(fieldClass, 'tabular-nums')}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                Считается по площади контура теста пряников набора. Уходит в формулу базовой спецификации 1С по артикулу.
              </p>
            </div>
          )
        )}
      </div>

      <div className="space-y-4">
        <section>
          <SectionTitle>Товар</SectionTitle>
          <Row label="Артикул">
            <span className="inline-flex items-center gap-1">
              <span className="font-mono">{product.article}</span>
              <CopyButton value={product.article} label="артикул" size="xs" />
            </span>
          </Row>
          <Row label="Цена категории">{formatPrice(product.price ?? product.category?.price)}</Row>
          <Row label="1С">
            <ValueOrNoData
              value={ONE_C_INFO[product.one_c_status || '']?.label || product.one_c_status}
              why="статус синхронизации не получен"
            />
          </Row>
        </section>

        <section>
          <SectionTitle source={manual || noDesign ? undefined : 'из макета'}>Макет-донор</SectionTitle>
          {manual ? (
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 dark:border-zinc-800 dark:bg-zinc-900">
              <Badge variant="gray">Без макета</Badge>
              <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                Изображения товара загружены вручную и не перерисовываются конвейером.
              </p>
            </div>
          ) : noDesign ? (
            <Badge variant="gray">Без макета</Badge>
          ) : design ? (
            <>
              <Row label="Код">
                <span className="inline-flex items-center gap-1">
                  <span className="font-mono">#{design.design_code}</span>
                  <CopyButton value={String(design.design_code)} label="код макета" size="xs" />
                </span>
              </Row>
              <Row label="Тип">
                <ValueOrNoData value={design.type} />
              </Row>
              <Row label="Бренд">
                <ValueOrNoData value={design.brand_ru} />
              </Row>
              <Row label="Повод">
                <ValueOrNoData value={design.occasion} />
              </Row>
              <Row label="Тематика">
                <ValueOrNoData
                  value={themeNumber !== null ? `№ ${themeNumber}${design.theme ? ` · ${design.theme}` : ''}` : design.theme}
                />
              </Row>
              {onOpenDesign && (
                <div className="mt-2">
                  <Button size="sm" variant="secondary" icon={<ExternalLink className="h-3.5 w-3.5" />} onClick={onOpenDesign}>
                    Открыть макет
                  </Button>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-500 dark:text-zinc-400">Без макета</p>
          )}
        </section>
      </div>
    </div>
  )
}
